import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Toast from '../components/Toast';
import { forceSessionLogout, shouldSkipForcedLogout } from '../utils/apiClient';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const hasActiveSession = () => {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return Boolean(token && token !== 'FIRST_TIME_LOGIN');
  } catch (_) {
    return false;
  }
};

/** Only force logout from authenticated app pages (not login/registration/verification). */
const shouldLogoutOnErrorToast = () => {
  if (typeof window === 'undefined') return false;
  if (!hasActiveSession()) return false;
  const path = window.location.pathname || '';
  return path.startsWith('/pages');
};

/**
 * Business "not enough money" toasts already have their own popup.
 * Do not force logout for those — only for programming / info failures.
 */
const isInsufficientFundsToast = (title, message) => {
  const text = `${title || ''} ${message || ''}`;
  if (!text.trim()) return false;
  return /enough amount|enough money|enough balance|insufficient(?:\s+balance)?|not enough|don['’]?t have enough|does(?:n['’]?t| not) have enough|balance is not enough|you_dont_have_enough|user_dont_have_enough|user_doesnt_have_enough|solde est insuffisant|pas suffisamment de fonds|insuffisant|رصيدك غير كاف|ليس لديه مبلغ كاف|مبلغ كاف/i.test(
    text
  );
};

/** User dismissed a PIN/password dialog or canceled a request — keep the session. */
const isUserCanceledToast = (title, message) => {
  const text = `${title || ''} ${message || ''}`;
  if (!text.trim()) return false;
  return /transaction canceled|transaction cancelled|exchange canceled|exchange cancelled|action canceled|action cancelled|request has been canceled|request has been cancelled/i.test(
    text
  );
};

const normalizeErrorOptions = (durationOrOptions, maybeOptions) => {
  if (durationOrOptions && typeof durationOrOptions === 'object') {
    return {
      duration: durationOrOptions.duration ?? 5000,
      skipLogout: Boolean(durationOrOptions.skipLogout),
    };
  }
  return {
    duration: typeof durationOrOptions === 'number' ? durationOrOptions : 5000,
    skipLogout: Boolean(maybeOptions && maybeOptions.skipLogout),
  };
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((type, title, message, duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type,
      title,
      message,
      isVisible: true,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const success = useCallback((title, message, duration) => {
    return showToast('success', title, message, duration);
  }, [showToast]);

  const warning = useCallback((title, message, duration) => {
    return showToast('warning', title, message, duration);
  }, [showToast]);

  const error = useCallback((title, message, durationOrOptions, maybeOptions) => {
    const { duration, skipLogout } = normalizeErrorOptions(durationOrOptions, maybeOptions);
    const id = showToast('error', title, message, duration);
    const keepSession =
      skipLogout ||
      isInsufficientFundsToast(title, message) ||
      isUserCanceledToast(title, message);
    // Programming / info failures still force logout. Cancel and insufficient-funds popups do not.
    if (!keepSession && shouldLogoutOnErrorToast()) {
      // Short delay so the toast can paint before redirect.
      // Skip if the user just left the app / phone browser — wait until they are back.
      setTimeout(() => {
        if (shouldSkipForcedLogout()) return;
        forceSessionLogout();
      }, 400);
    }
    return id;
  }, [showToast]);

  useEffect(() => {
    const onFcmForegroundToast = (e) => {
      const title = e?.detail?.title || 'Notification';
      const message = e?.detail?.body != null ? String(e.detail.body) : '';
      success(title, message, 6000);
    };
    window.addEventListener('mfg:fcm-foreground-toast', onFcmForegroundToast);
    return () => window.removeEventListener('mfg:fcm-foreground-toast', onFcmForegroundToast);
  }, [success]);

  return (
    <ToastContext.Provider value={{ showToast, success, warning, error, removeToast }}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            type={toast.type}
            title={toast.title}
            message={toast.message}
            isVisible={toast.isVisible}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
